// src/pages/AdminSchemaPage.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";

const TYPE_OPTIONS = [
  "TEXT",
  "INTEGER",
  "REAL",
  "BLOB",
  "NUMERIC",
  "VARCHAR(255)",
  "DATE",
  "DATETIME",
  "BOOLEAN",
];

function isValidIdent(name) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(name || ""));
}

function apiErrText(e) {
  const status = e?.response?.status;
  const body = e?.response?.data;
  const msg =
    body?.error ||
    body?.message ||
    (typeof body === "string" ? body : null) ||
    e?.message ||
    "Request failed";
  return status ? `${status}: ${msg}` : msg;
}

export default function AdminSchemaPage() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // create table
  const [newTableName, setNewTableName] = useState("");
  const [newTableWithId, setNewTableWithId] = useState(true);

  // add column
  const [colName, setColName] = useState("");
  const [colType, setColType] = useState("TEXT");
  const [colNotNull, setColNotNull] = useState(false);
  const [colDefault, setColDefault] = useState("");

  // change type
  const [chgCol, setChgCol] = useState("");
  const [chgType, setChgType] = useState("TEXT");

  // create index
  const [idxCol, setIdxCol] = useState("");
  const [idxUnique, setIdxUnique] = useState(false);

  const reloadTables = async () => {
    const { data } = await api.get("/admin/schema/tables");
    const list = Array.isArray(data?.tables) ? data.tables : [];
    setTables(list);
    if (!selected && list.length) setSelected(list[0].name);
  };

  const reloadDetail = async (table) => {
    if (!table) return;
    const { data } = await api.get(`/admin/schema/table/${table}`);
    setDetail(data || null);

    const firstCol = data?.cols?.[0]?.name || "";
    setChgCol((prev) => prev || firstCol);
    setIdxCol((prev) => prev || firstCol);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setBusy(true);
        setMsg("");
        await reloadTables();
      } catch (e) {
        if (alive) setMsg(apiErrText(e));
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selected) return;
      try {
        setBusy(true);
        setMsg("");
        await reloadDetail(selected);
      } catch (e) {
        if (alive) setMsg(apiErrText(e));
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const tableOptions = useMemo(() => {
    return (tables || []).map((t) => ({ v: t.name, label: t.name }));
  }, [tables]);

  const createTable = async () => {
    setMsg("");
    const name = newTableName.trim();
    if (!isValidIdent(name)) {
      return setMsg("Invalid table name (letters/numbers/underscore, cannot start with number).");
    }

    setBusy(true);
    try {
      await api.post("/admin/schema/table", {
        name,
        withId: newTableWithId,
        columns: [],
      });
      setNewTableName("");
      await reloadTables();
      setSelected(name);
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const addColumn = async () => {
    setMsg("");
    const table = selected;
    const name = colName.trim();
    if (!table) return setMsg("No table selected.");
    if (!isValidIdent(name)) return setMsg("Invalid column name.");

    // If NOT NULL, SQLite effectively needs a DEFAULT for existing rows
    if (colNotNull && !String(colDefault || "").length) {
      return setMsg("If NOT NULL is checked, provide a DEFAULT value.");
    }

    setBusy(true);
    try {
      await api.post("/admin/schema/column", {
        table,
        name,
        type: colType,
        notnull: !!colNotNull,
        default: String(colDefault || ""),
      });
      setColName("");
      setColDefault("");
      setColNotNull(false);
      await reloadDetail(table);
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const changeType = async () => {
    setMsg("");
    const table = selected;
    if (!table) return setMsg("No table selected.");
    if (!chgCol) return setMsg("Pick a column.");
    if (!chgType) return setMsg("Pick a type.");

    const ok = window.confirm(
      `SQLite must rebuild the table to change a column type.\n\nTable: ${table}\nColumn: ${chgCol}\nNew Type: ${chgType}\n\nProceed?`
    );
    if (!ok) return;

    setBusy(true);
    try {
      await api.post("/admin/schema/column/type", {
        table,
        column: chgCol,
        newType: chgType,
      });
      await reloadDetail(table);
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const createIndex = async () => {
    setMsg("");
    const table = selected;
    if (!table) return setMsg("No table selected.");
    if (!idxCol) return setMsg("Pick a column.");

    setBusy(true);
    try {
      await api.post("/admin/schema/index", {
        table,
        column: idxCol,
        unique: !!idxUnique,
      });
      setIdxUnique(false);
      await reloadDetail(table);
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container space-y">
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 0.2 }}>Admin • Schema</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            View tables/columns • add tables/fields • change field types (SQLite rebuild).
          </div>
        </div>
      </div>

      {msg && <div className="muted" style={{ whiteSpace: "pre-wrap" }}>{msg}</div>}
      {busy && <div className="muted">Working…</div>}

      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Tables</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select className="input" style={{ width: 260 }} value={selected} onChange={(e) => setSelected(e.target.value)}>
            {tableOptions.map((t) => (
              <option key={t.v} value={t.v}>
                {t.label}
              </option>
            ))}
          </select>

          <button className="btn-outline" type="button" onClick={() => reloadTables().catch(() => {})} disabled={busy}>
            Refresh
          </button>
        </div>

        <hr style={{ opacity: 0.15, margin: "12px 0" }} />

        <div style={{ fontWeight: 800, marginBottom: 10 }}>Create new table</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            className="input"
            style={{ width: 260 }}
            placeholder="table_name"
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
          />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={newTableWithId} onChange={(e) => setNewTableWithId(e.target.checked)} />
            Include id INTEGER PRIMARY KEY
          </label>
          <button className="btn" type="button" onClick={createTable} disabled={busy}>
            Create
          </button>
        </div>

        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Tables are created empty (besides optional <code>id</code>). Add columns below.
        </div>
      </div>

      <div className="card">
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Columns for: {selected || "(none)"}</div>

        {!detail?.cols?.length ? (
          <div className="muted">No columns loaded.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["name", "type", "notnull", "dflt_value", "pk"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 10px",
                        borderBottom: "1px solid rgba(255,255,255,0.10)",
                        opacity: 0.8,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.cols.map((c) => (
                  <tr key={c.name} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={{ padding: "10px 10px", whiteSpace: "nowrap", fontWeight: 700 }}>{c.name}</td>
                    <td style={{ padding: "10px 10px", whiteSpace: "nowrap" }}>{c.type || ""}</td>
                    <td style={{ padding: "10px 10px" }}>{c.notnull}</td>
                    <td style={{ padding: "10px 10px" }}>{c.dflt_value == null ? "" : String(c.dflt_value)}</td>
                    <td style={{ padding: "10px 10px" }}>{c.pk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <hr style={{ opacity: 0.15, margin: "12px 0" }} />

        <div style={{ fontWeight: 800, marginBottom: 10 }}>Add column</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            className="input"
            style={{ width: 220 }}
            placeholder="column_name"
            value={colName}
            onChange={(e) => setColName(e.target.value)}
          />
          <select className="input" style={{ width: 200 }} value={colType} onChange={(e) => setColType(e.target.value)}>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={colNotNull} onChange={(e) => setColNotNull(e.target.checked)} />
            NOT NULL
          </label>
          <input
            className="input"
            style={{ width: 260 }}
            placeholder="default (optional; required if NOT NULL)"
            value={colDefault}
            onChange={(e) => setColDefault(e.target.value)}
          />
          <button className="btn" type="button" onClick={addColumn} disabled={busy}>
            Add
          </button>
        </div>

        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          SQLite supports <code>ALTER TABLE … ADD COLUMN</code>. No adding PK/UNIQUE via ALTER.
        </div>

        <hr style={{ opacity: 0.15, margin: "12px 0" }} />

        <div style={{ fontWeight: 800, marginBottom: 10 }}>Change column type (rebuild table)</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select className="input" style={{ width: 260 }} value={chgCol} onChange={(e) => setChgCol(e.target.value)}>
            {(detail?.cols || []).map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>

          <select className="input" style={{ width: 200 }} value={chgType} onChange={(e) => setChgType(e.target.value)}>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <button className="btn" type="button" onClick={changeType} disabled={busy}>
            Change Type
          </button>
        </div>

        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Under the hood: temp table → copy rows (casts changed column) → drop old → rename temp → recreate indexes/triggers.
        </div>

        <hr style={{ opacity: 0.15, margin: "12px 0" }} />

        <div style={{ fontWeight: 800, marginBottom: 10 }}>Create index</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select className="input" style={{ width: 260 }} value={idxCol} onChange={(e) => setIdxCol(e.target.value)}>
            {(detail?.cols || []).map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={idxUnique} onChange={(e) => setIdxUnique(e.target.checked)} />
            UNIQUE
          </label>

          <button className="btn" type="button" onClick={createIndex} disabled={busy}>
            Create Index
          </button>
        </div>
      </div>
    </div>
  );
}